/**
 * Where the messages are: a stacked bar across the four escalation lanes.
 *
 * The lanes are an *ordered* scale, not four unrelated categories - queued, retrying,
 * cooling off, parked is how far down the escalation a message has got - so they wear
 * a single-hue ordinal ramp rather than four categorical hues. More escalated is more
 * prominent against the surface, in both modes.
 *
 * Segments are separated by a 2px surface gap, never a border: a stroke around a mark
 * adds ink that is not data. An inline label appears only where the segment is
 * comfortably wide enough to hold it - otherwise the legend below carries the value,
 * which is why the legend is always present rather than only when it is needed.
 */

import { cn } from '../../lib/cn'
import { formatCount } from '../../lib/format'
import { QUEUE_LANES } from '../../lib/status'
import type { QueueTotals } from '../../api/types'
import { Tooltip, TooltipRow } from '../ui/Tooltip'

/** Below this width an inline label would be clipped, so it moves to the legend. */
const MIN_LABEL_WIDTH_PCT = 18

interface PipelineBarProps {
  totals: QueueTotals
  className?: string
  /** Heading for the figure. The bar has no axis, so this names what is plotted. */
  title?: string
}

export function PipelineBar({ totals, className, title }: PipelineBarProps) {
  const lanes = QUEUE_LANES.map((lane) => ({
    ...lane,
    count: Number(totals[lane.key] ?? 0),
  }))
  const total = lanes.reduce((sum, lane) => sum + lane.count, 0)

  return (
    <figure className={cn('min-w-0', className)}>
      {title ? (
        <figcaption className="mb-2 flex items-baseline justify-between gap-3">
          <span className="text-xs font-semibold text-ink-secondary">{title}</span>
          <span className="tabular text-xs text-ink-muted">
            {formatCount(total)} message{total === 1 ? '' : 's'}
          </span>
        </figcaption>
      ) : null}

      {total === 0 ? (
        <div
          className="flex h-5 items-center rounded-md px-2 text-[11px] text-ink-muted"
          style={{ backgroundColor: 'var(--color-surface-sunken)' }}
        >
          Every lane empty — nothing queued, retrying, cooling off or parked
        </div>
      ) : (
        <div className="flex h-5 w-full gap-[2px] overflow-hidden rounded-md">
          {lanes.map((lane) => {
            if (lane.count === 0) return null
            const widthPct = (lane.count / total) * 100
            return (
              <Tooltip
                as="div"
                key={lane.key}
                className="h-full shrink-0"
                // The width belongs on the flex child - see Tooltip's `style` note.
                style={{ width: `${widthPct}%`, minWidth: 3 }}
                content={
                  <div className="space-y-1">
                    <TooltipRow
                      value={formatCount(lane.count)}
                      label={lane.label.toLowerCase()}
                      swatch={lane.fill}
                    />
                    <p className="text-ink-secondary">{lane.description}</p>
                  </div>
                }
              >
                <div
                  className="flex h-full w-full items-center justify-center"
                  style={{ backgroundColor: lane.fill }}
                >
                  {widthPct >= MIN_LABEL_WIDTH_PCT ? (
                    // Inside a coloured fill, the label picks its ink by the fill's
                    // luminance - the one place text may sit on a series colour.
                    <span
                      className="tabular px-1 text-[11px] font-semibold"
                      style={{ color: labelInkFor(lane.key) }}
                    >
                      {lane.count}
                    </span>
                  ) : null}
                </div>
              </Tooltip>
            )
          })}
        </div>
      )}

      {/* Always present: identity never rests on colour-matching alone. */}
      <ul className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1.5">
        {lanes.map((lane) => (
          <li key={lane.key} className="flex items-center gap-1.5 text-[11px]">
            <span
              aria-hidden
              className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
              style={{ backgroundColor: lane.fill }}
            />
            <span className="text-ink-secondary">{lane.label}</span>
            <span className="tabular font-semibold text-ink">{lane.count}</span>
          </li>
        ))}
      </ul>
    </figure>
  )
}

/**
 * White on the two darker lane steps, ink on the two lighter ones.
 *
 * The ramp reverses between modes, so this cannot be a fixed per-lane choice - it is
 * resolved from the same `light-dark()` pairing the tokens use, which keeps the label
 * legible on whichever step that lane is wearing.
 */
function labelInkFor(lane: string): string {
  const darkStepInLightMode = lane === 'cooling_off' || lane === 'parked'
  return darkStepInLightMode
    ? 'light-dark(#ffffff, #0b0b0b)'
    : 'light-dark(#0b0b0b, #ffffff)'
}
